import React from "react";
import { Box, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { DatePreset, DateRange } from "./useDateRange";

interface DateRangeNavProps {
  dateRange: DateRange;
}

const PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom range" },
];

interface IFieldGroupProps {
  caption: string;
  children: React.ReactNode;
}

// Every field (Select or DatePicker) is wrapped the same way - a caption line
// followed by a plain, unlabeled control - so all boxes share identical layout
// and line up regardless of each control's own internal spacing.
const FieldGroup = ({ caption, children }: IFieldGroupProps) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", lineHeight: "18px", mb: 0.5 }}
    >
      {caption}
    </Typography>
    {children}
  </Box>
);

export const DateRangeNav = ({ dateRange }: DateRangeNavProps) => {
  const { preset, label, setPreset, customStart, customEnd, setCustomStart, setCustomEnd } = dateRange;

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap">
      <FieldGroup caption="Range">
        <Select
          size="small"
          value={preset}
          onChange={(event: SelectChangeEvent) => setPreset(event.target.value as DatePreset)}
          sx={{ minWidth: 160 }}
        >
          {PRESET_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      {preset === "custom" ? (
        <>
          <FieldGroup caption="Start">
            <DatePicker
              value={customStart}
              onChange={setCustomStart}
              format="DD/MM/YYYY"
              maxDate={customEnd ?? dayjs()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: 150 },
                  inputProps: { "aria-label": "Start date" },
                },
              }}
            />
          </FieldGroup>
          <FieldGroup caption="End">
            <DatePicker
              value={customEnd}
              onChange={setCustomEnd}
              format="DD/MM/YYYY"
              minDate={customStart ?? undefined}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: 150 },
                  inputProps: { "aria-label": "End date" },
                },
              }}
            />
          </FieldGroup>
        </>
      ) : (
        <FieldGroup caption="Period">
          <Typography variant="body2" sx={{ lineHeight: "40px", minWidth: 110 }}>
            {label}
          </Typography>
        </FieldGroup>
      )}
    </Stack>
  );
};
